/**
 * SkinnrMuiProvider.tsx - Material UI Provider integrated with Skinnr
 * 
 * CHANGE HISTORY:
 * - 251025_1728 danielbeer: Initial MUI provider integration with Skinnr
 * 
 * Wraps Material UI ThemeProvider with Skinnr theme system for unified theming
 */

import React, { useEffect, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { SkinnrProvider, useSkinnr } from './SkinnrContext';
import { createSkinnrMuiTheme, updateMuiTheme, SkinnrThemeMode } from './mui-theme';

/**
 * Props for SkinnrMuiProvider
 */
export interface SkinnrMuiProviderProps {
  /** Child components */
  children: React.ReactNode;
  /** Initial theme mode */
  theme?: SkinnrThemeMode;
  /** Maximum number of snackbars to show at once */
  maxSnack?: number;
  /** Auto-hide duration for snackbars (ms) */
  autoHideDuration?: number;
  /** Snackbar anchor position */
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

/**
 * Internal component that wraps MUI ThemeProvider
 * Must be inside SkinnrProvider to access theme context
 */
const MuiThemeWrapper: React.FC<{
  children: React.ReactNode;
  maxSnack?: number;
  autoHideDuration?: number;
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
}> = ({ children, maxSnack = 3, autoHideDuration = 3000, anchorOrigin }) => {
  const { currentTheme } = useSkinnr();
  const [muiTheme, setMuiTheme] = useState(() => createSkinnrMuiTheme(currentTheme as SkinnrThemeMode));

  // Update MUI theme when Skinnr theme changes
  useEffect(() => {
    const newTheme = updateMuiTheme(currentTheme as SkinnrThemeMode);
    setMuiTheme(newTheme);
  }, [currentTheme]);

  // Memoize theme to prevent unnecessary re-renders
  const theme = useMemo(() => muiTheme, [muiTheme]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={maxSnack}
        autoHideDuration={autoHideDuration}
        anchorOrigin={anchorOrigin || { vertical: 'top', horizontal: 'right' }}
        preventDuplicate
      >
        {children}
      </SnackbarProvider>
    </MuiThemeProvider>
  );
};

/**
 * SkinnrMuiProvider - Combined Skinnr + Material UI Provider
 * 
 * Provides both Skinnr theming and Material UI theming to child components.
 * Automatically syncs MUI theme with Skinnr theme changes.
 * 
 * @example
 * ```tsx
 * import { SkinnrMuiProvider } from '@opcentral/skinnr';
 * 
 * function App() {
 *   return (
 *     <SkinnrMuiProvider theme="dark">
 *       <YourApp />
 *     </SkinnrMuiProvider>
 *   );
 * }
 * ```
 * 
 * @example With custom snackbar settings
 * ```tsx
 * <SkinnrMuiProvider
 *   theme="light"
 *   maxSnack={5}
 *   autoHideDuration={5000}
 *   anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
 * >
 *   <YourApp />
 * </SkinnrMuiProvider>
 * ```
 */
export const SkinnrMuiProvider: React.FC<SkinnrMuiProviderProps> = ({
  children,
  theme = 'light',
  maxSnack,
  autoHideDuration,
  anchorOrigin,
}) => {
  return (
    <SkinnrProvider initialTheme={theme}>
      <MuiThemeWrapper
        maxSnack={maxSnack}
        autoHideDuration={autoHideDuration}
        anchorOrigin={anchorOrigin}
      >
        {children}
      </MuiThemeWrapper>
    </SkinnrProvider>
  );
};

/**
 * Hook to access both Skinnr and MUI theme
 * 
 * @example
 * ```tsx
 * import { useSkinnrMui } from '@opcentral/skinnr';
 * 
 * function MyComponent() {
 *   const { theme, setTheme, muiTheme } = useSkinnrMui();
 *   
 *   return (
 *     <button onClick={() => setTheme('dark')}>
 *       Switch to Dark Mode
 *     </button>
 *   );
 * }
 * ```
 */
export const useSkinnrMui = () => {
  const skinnr = useSkinnr();
  
  return {
    ...skinnr,
    // Alias for consistency
    theme: skinnr.currentTheme,
    setTheme: skinnr.setTheme,
  };
};

export default SkinnrMuiProvider;
